import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { HeaderService } from "src/app/components/header/header.service";
import { AgGridRequestService } from "src/app/services/ag-grid-request.service";
import { PricingModel, RepriceModelSku } from "../../../models/priceyard.model";
import { Subject } from "rxjs";
import { BehaviorSubject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PriceyardService implements OnDestroy {
  private url = "api/Repricer";
  private headers = new HttpHeaders({ "Content-Type": "application/json" });
  accounts;
  openSidenav$ = new Subject<any>();
  isPriceyardPanel : boolean =false;
  modelId: number;
  private destroy$ = new Subject<void>();
  options = { headers: this.headers };
  PriceCalculatorId;
  constructor(
    private http: HttpClient,
    private headerService: HeaderService,
    private agGridRequestService: AgGridRequestService
  ) {
    this.headerService.accountsSelected.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      this.accounts = e;
    });
  }

  GetPriceModel() {
    return this.http.get<PricingModel[]>(
      `/${this.url}/ListRepriceModels/`,
      this.options
    );
  }

  GetModelSkuList(id: number, marketplaceType) {
    return this.http.get<RepriceModelSku[]>(
      `/${this.url}/ModelSkuList?Id=${id}&MarketplaceType=${marketplaceType}`,
      this.options
    );
  }

  SavePriceModel(priceModel: any) {
    return this.http.post(
      `/${this.url}/SaveRepriceModel2/`,
      priceModel,
      this.options
    );
  }

  GetRepriceModel(id, marketplaceType) {
    return this.http.get(
      `/${this.url}/GetRepricingModel2/?Id=${id}&MarketplaceType=${marketplaceType}`,
      this.options
    );
  }

  GetSkuList(body, columnDefs, globalSearch?) {
    const filters = {}
    if (this.accounts) {
      filters['Account'] = {
          filterType: "set",
          values: [this.accounts[0]],
        };
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      globalSearch,
      filters
    );
    return this.http.post(`/${this.url}/GetSkuList1`, payload, this.options);
  }

  getSkuDetails(accountSkuId, account?, fulfillmentType?) {
    return this.http.post(
      `/${this.url}/GetSkuDetails`,
      { accountSkuId, account, fulfillmentType },
      this.options
    );
  }

  UpdateMinMaxPricing(
    sku: string,
    account: string,
    minPrice: number,
    maxPrice: number
  ) {
    return this.http.put(
      `/${this.url}/updateminmaxpricing?sku=${sku}&account=${account}&minPrice=${minPrice}&maxPrice=${maxPrice}`,
      this.options
    );
  }

  EditNote(payload) {
    return this.http.post(
      `/${this.url}/EditNote`,
      payload,
      this.options
    );
  }

  ChangeMinPrice(sku, account, price, fulfillmentType) {
    return this.http.post(
      `/${this.url}/ChangeMinPrice`,
      { sku: [sku], account, price, fulfillmentType },
      this.options
    );
  }

  ChangeMaxPrice(sku, account, price, fulfillmentType) {
    return this.http.post(
      `/${this.url}/ChangeMaxPrice`,
      { sku: [sku], account, price, fulfillmentType },
      this.options
    );
  }

  ChangeCost(sku, account, cost, fulfillmentType) {
    return this.http.post(
      `/${this.url}/ChangeCost`,
      { sku: [sku], account, cost, fulfillmentType },
      this.options
    );
  }

  changePriceFactor(skus) {
    // const reqBody = { skus, account, fulfillmentType };
    // for (const propName in reqBody) {
    //   if (reqBody[propName] === null || reqBody[propName] === undefined) {
    //     delete reqBody[propName];
    //   }
    // }
    return this.http.post(
      `/${this.url}/ChangePriceFactor`,
      { skus },
      this.options
    );
  }

  changePriceFactorP(skus) {
    return this.http.post(
      `/${this.url}/ChangePriceFactorP`,
      { skus },
      this.options
    );
  }

  UpdateSkuPricingModel(sku: string[], account, pricingModel) {
    return this.http.post(
      `/${this.url}/UpdateSkuPricingModel`,
      { skus: sku, account, pricingModel },
      this.options
    );
  }

  UpdateSkuPricingActive(sku, account, pricingActive) {
    return this.http.post(
      `/${this.url}/UpdateSkuPricingActive`,
      { sku, account, pricingActive },
      this.options
    );
  }

  DownloadSkus(account: string) {
    return this.http.get(
      `/api/Shipments/DownloadSkus?Account=${account}`,
      this.options
    );
  }

  SkuCompetition(asin) {
    return this.http.get(
      `/${this.url}/SkuCompetition?Asin=${asin}`,
      this.options
    );
  }

  GetCompetitionList(body, columnDefs) {
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      undefined,
      {
        Account: {
          filterType: "set",
          values: [this.accounts[0]],
        },
      }
    );

    return this.http.post(
      `/${this.url}/GetCompetitionList`,
      payload,
      this.options
    );
  }

  SkuRepriceHistory(sku, account) {
    return this.http.get(
      `/${this.url}/SkuRepriceHistory?sku=${sku}&account=${account}`,
      this.options
    );
  }

  SkuRepriceHistoryGrid(body, columnDefs, globalSearch?, optionalFilter?) {
    if (optionalFilter) {
      body.filterModel[optionalFilter.field] = {
        filter: optionalFilter.filter,
        filterType: "text",
        type: "equals",
      };
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      globalSearch,
      {
        Account: {
          filterType: "set",
          values: [this.accounts[0]],
        },
      }
    );
    return this.http.post(
      `/${this.url}/SkuRepriceHistoryGrid`,
      payload,
      this.options
    );
  }

  CompetitionListDetail(body, columnDefs, optionalFilter?) {
    if (optionalFilter?.length) {
      optionalFilter.forEach((res) =>{
        body.filterModel[res.field] = {
          filter: res.filter,
          filterType: "text",
          type: "equals",
        };
      })
    }
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columnDefs,
      undefined,
      {
        Account: {
          filterType: "set",
          values: [this.accounts[0]],
        },
      }
    );
    return this.http.post(
      `/${this.url}/CompetitionListDetail`,
      payload,
      this.options
    );
  }

  getPricingSettings(account: string) {
    return this.http.get(
      `/${this.url}/GetPricingSettings?AccountName=${account}`,
      this.options
    );
  }

  updatePricingSettings(pricing) {
    return this.http.post(
      `/${this.url}/UpdatePricingSettings`,
      pricing,
      this.options
    );
  }

  currencyFormatter(currency, sign) {
    if (isNaN(currency)) {
      return;
    }
    const sansDec = currency.toFixed(2);
    const formatted = sansDec.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return sign + `${formatted}`;
  }

  GetSkuListExport(filters, columns, globalSearch, page) {
    const body = {
      filterModel: filters,
    };

    const filter = {
      Account: {
        filterType: "set",
        values: [this.accounts[0]],
      },
    };
    /*if (page === "replenish") {
      filter["FulfillmentType"] = {
        filterType: "set",
        values: ["Fba"],
      };
    }*/
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      filter
    );
    const imageIndex = payload.columns.indexOf("Image");
    if (imageIndex > -1) {
      payload.columns.splice(imageIndex, 1);
    }

    return this.http.post(`/${this.url}/GetSkuListExport`, payload, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  SaveSellersName(body: {
    amazonId: string;
    name?: string;
    ignore?: boolean;
    ignoreWithBb?: boolean;
  }) {
    return this.http.post(`/${this.url}/SaveSellersName`, body, this.options);
  }

  GetItemListExport(filters, columns, globalSearch,isExport) {
    const body = {
      filterModel: filters,
    };
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      null,
      null,
      isExport
    );

    return this.http.post(`/${this.url}/GetItemListExport`, payload, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  GetOrdersListExport(filters, columns, globalSearch,isExport,account?) {
    const body = {
      filterModel: filters,
    };
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      null,
      null,
      isExport
    );
    payload.loadAll= Object.keys(filters).length === 0;
    return this.http.post(`/${this.url}/GetOrdersListExport`, payload, {
      headers: this.headers,
      observe: "response",
      responseType: "blob",
    });
  }

  GetSimpleListExportBackground(filters, columns, globalSearch, page,isExport?,sortModel?) {
    const body = {
      filterModel: filters,
      sortModel:sortModel
    };

    const filter = {
      Account: {
        filterType: "set",
        values: [this.accounts[0]],
      },
    };
    const payload = this.agGridRequestService.transformAgGridRequest(
      body,
      columns,
      globalSearch,
      filter,
      null,
      isExport
    );
    const imageIndex = payload.columns.indexOf("Image");
    if (imageIndex > -1) {
      payload.columns.splice(imageIndex, 1);
    }

    return this.http.post(`/${this.url}/GetSimpleListExportBackgroundLocal`, payload, this.options);
  }

  GetPricePulseBuyBoxWinningSummary() {
    return this.http.post(`/${this.url}/GetPricePulseBuyBoxWinningSummary`,this.options);
  }

  GetPricePulseSalesSummary(payload) {
    return this.http.get(
      `/${this.url}/GetPricePulseSalesSummary?StartDate=${payload.startDate}&EndDate=${payload.endDate}`,
      this.options
    );
  }

  BlackBoxWinningPricingListSummary(payload){
    return this.http.post(
      `/${this.url}/BuyBoxWinningPricingListSummary`, payload,
      this.options
    );
  }
  
  getMappedPriceSettingsByModelId(payload) {
    return this.http.post(
      `/${this.url}/GetMappedPriceSettingsByModelId`,
      payload,
      this.options
    );
  }

  SalesPricingListSummary(payload){
    return this.http.post(
      `/${this.url}/SalesPricingListSummary`, payload,
      this.options
    );
  }

  addMappedPriceSettings(payload) {
    return this.http.post(
      `/${this.url}/AddUpdateMappedPriceSettings`,
      payload,
      this.options
    );
  }

  CompetitionPricingListSummary(payload){
    return this.http.post(
      `/${this.url}/CompetitionPricingListSummary`, payload,
      this.options
    );
  }

  RepriceActivityPricingListSummary(payload){
    return this.http.post(
      `/${this.url}/RepriceActivityPricingListSummary`, payload,
      this.options
    );
  }

  GetTopPricePulseItems(){
    return this.http.get(
      `/${this.url}/GetTopPricePulseItems`,
      this.options
    );
  }

  GetTopTenItemRepricedCompetitionList(){
    return this.http.post(
      `/${this.url}/GetTopTenItemRepricedCompetitionList`,
      this.options
    );
  }

  GetRepricedCompetitionItemsList(payload){
    return this.http.post(
      `/${this.url}/GetRepricedCompetitionItemsList`, payload,
      this.options
    );
  }

  GetFeeCalculationFees(payload){
    return this.http.get(
      `/${this.url}/GetFeeCalculationFees?sku=${payload.sku}&AccountSkuId=${payload.accountSkuId}`,
      this.options
    );
  }

  GetWinningBuyBoxItemsList(){
    return this.http.get(
      `/${this.url}/GetWinningBuyBoxItemsList`,
      this.options
    );
  }
  GetPricePulseRepriceActivitySummary(payload){
    return this.http.get(
      `/${this.url}/GetPricePulseRepriceActivitySummary?StartDate=${payload.startDate}&EndDate=${payload.endDate}`,
      this.options
    );
  }

  GetCostFeesDetails(payload){
    return this.http.get(
      `/${this.url}/GetCostFeesDetails?FulfilmentType=${payload.fulfilmentType}&AccountSkuId=${payload.accountSkuId}`,
      this.options
    );
  }
  GetPricePulseCompetitionSummary(payload){
    return this.http.get(
      `/${this.url}/GetPricePulseCompetitionSummary?StartDate=${payload.startDate}&EndDate=${payload.endDate}`,
      this.options
    );
  }

  
  GetSummaryByAccountSkuId(payload){
    return this.http.post(
      `/${this.url}/GetSummaryByAccountSkuId`, payload,
      this.options
    );
  }
  GetPricePulseBuyBoxWinningSummaryBarChart(payload){
    return this.http.get(
      `/${this.url}/GetPricePulseBuyBoxWinningSummaryBarChart?StartDate=${payload.startDate}&EndDate=${payload.endDate}`,
      this.options
    );
  }

  ProcessBulkMinandMaxPrice(payload){
    return this.http.post(
      `/${this.url}/ProcessBulkMinandMaxPriceandFeeCalculation`, payload,
      this.options
    );
  }

  deleteMappedPriceSettingsByMapModelId(payload) {
    return this.http.post(
      `/${this.url}/DeleteMappedPriceSettingsByMapModelId`,
      payload,
      this.options
    );
  } 

  AddUpdateMappedPriceGlobalSettings(payload){
    return this.http.post(
      `/${this.url}/AddUpdateMappedPriceGlobalSettings`, payload,
      this.options
    );
  }
  GetPricePulseRepriceActivityBarChartSummary(payload){
    return this.http.get(
      `/${this.url}/GetPricePulseRepriceActivityBarChartSummary?StartDate=${payload.startDate}&EndDate=${payload.endDate}`,
      this.options
    );
  }  

  GetNotificationForPricePulse(payload){
    return this.http.post(
      `/${this.url}/GetNotificationForPricePulse`, payload,
      this.options
    );
  }
  
  GetSkusForPoId(PoId) {
    return this.http.get(
      `/${this.url}/GetSkusForPoId/?PoId=${PoId}`,
      this.options
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

}
